import React, {useEffect, useMemo, useState} from "react";
import {Container} from "react-bootstrap";
import {PiCircleFill} from "react-icons/pi";
import NutzungLayout from "../../Components/Nutzung/NutzungLayout.jsx";
import useNutzungFilter from "../../Components/Nutzung/useNutzungFilter.js";
import useStatisticsData from "../../Components/Statistics/useStatisticsData.js";
import usePollingWhenVisible from "../../hooks/usePollingWhenVisible.js";
import {bereichFarbe, formatDuration, formatTimeDE} from "../../Components/Nutzung/format.js";
import {BereichLink, MitarbeiterLink, SeiteLink} from "../../Components/Nutzung/Links.jsx";
import NutzungWidget from "../../Components/Nutzung/NutzungWidget.jsx";

const URL = "/nutzung/daten/wege-jetzt";
const TAKT_MS = 15000;

/**
 * Abrufadresse mit Querystring - "jetzt" kennt wie bei useNutzungLive
 * keinen Zeitraum (von/bis machen bei einer Momentaufnahme keinen Sinn),
 * Mitarbeiter- und Bereichsfilter engen dagegen sinnvoll ein, wessen
 * aktueller Weg gezeigt wird.
 */
function baueUrl(filters) {
    const params = new URLSearchParams();
    if (filters.user_id) {
        params.set("user_id", filters.user_id);
    }
    if (filters.bereich) {
        params.set("bereich", filters.bereich);
    }
    const qs = params.toString();
    return qs ? `${URL}?${qs}` : URL;
}

/**
 * Die Spezifikation (Abschnitt 3) beschreibt die Antwort nur in Prosa als
 * "je angemeldetem Mitarbeiter die letzten fünf Stationen (gekürzter
 * Wegverlauf) + aktueller Stand" - ohne den Namen des Felds mit der
 * Stationsliste festzulegen (anders als beim Wegverlauf-Endpunkt selbst,
 * dessen Feldnamen bindend sind). Angelehnt an dessen Terminologie wird
 * hier "stationen" erwartet; falls das Backend einen anderen Schlüssel
 * wählt, greift ein Rückfall, statt die Karte einfach leer zu lassen.
 */
function stationenVon(person) {
    const liste = person.stationen || person.wegverlauf || person.letzte_stationen;
    return Array.isArray(liste) ? liste : [];
}

/** Kurzform einer einzelnen Station - ohne Aufklappen (das leistet bereits
 * der vollständige Wegverlauf im Mitarbeiterprofil), dafür kompakt genug
 * für mehrere Karten nebeneinander. */
function MiniStation({station, istLetzte}) {
    const zeittext = station.ist_aktuell
        ? `seit ${formatTimeDE(station.von)} Uhr`
        : `${formatTimeDE(station.von)}–${formatTimeDE(station.bis)} Uhr`;
    const punktfarbe = station.ist_aktuell ? "#2e8b57" : "#c3a9ee";

    return (
        <div
            className="position-relative ps-3"
            style={{
                borderLeft: istLetzte ? "2px solid transparent" : "2px solid #e4e8ed",
                marginLeft: 3,
                paddingBottom: istLetzte ? 0 : ".65rem",
            }}
        >
            <span
                className={`position-absolute rounded-circle ${station.ist_aktuell ? "nutzung-puls" : ""}`}
                style={{
                    left: -5,
                    top: 4,
                    width: 8,
                    height: 8,
                    background: punktfarbe,
                    border: "2px solid #fff",
                    boxShadow: `0 0 0 1px ${punktfarbe}`,
                }}
                aria-hidden="true"
            />
            <div className="small text-muted" style={{fontVariantNumeric: "tabular-nums"}}>{zeittext}</div>
            <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="fw-semibold small">
                    <SeiteLink route={station.route || station.seite} className="text-decoration-none text-body" />
                </span>
                <span
                    className="badge fw-normal"
                    style={{backgroundColor: `${bereichFarbe(station.bereich)}22`, color: bereichFarbe(station.bereich)}}
                >
                    <BereichLink bereich={station.bereich} className="text-decoration-none" style={{color: "inherit"}} />
                </span>
            </div>
            {!station.ist_aktuell && (
                <div className="small text-muted">{formatDuration(station.dauer_sekunden)}</div>
            )}
        </div>
    );
}

/** Eine Karte je angemeldeter Person: Kopfzeile mit Name und aktuellem
 * Stand, darunter die letzten Stationen als kompakte Zeitleiste. */
function PersonKarte({person}) {
    const stationen = stationenVon(person);
    if (stationen.length === 0) {
        return null;
    }
    const aktuell = stationen.find((station) => station.ist_aktuell) || stationen[0];

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                    <div className="fw-semibold">
                        <MitarbeiterLink id={person.user_id} name={person.name} />
                    </div>
                    {aktuell?.ist_aktuell && (
                        <span className="small d-inline-flex align-items-center gap-1" style={{color: "#2e7d4f"}}>
                            <PiCircleFill className="nutzung-puls" size={9} aria-hidden="true" />
                            aktiv
                        </span>
                    )}
                </div>
                <div className="d-flex flex-column">
                    {stationen.map((station, index) => (
                        <MiniStation key={`${station.von}-${index}`} station={station} istLetzte={index === stationen.length - 1} />
                    ))}
                </div>
            </div>
        </div>
    );
}

/**
 * "Aktuelle Wege" - für jede gerade angemeldete Person die letzten
 * Stationen ihres heutigen Wegs durchs System, aktualisiert alle 15
 * Sekunden (nur solange der Tab sichtbar ist). Ist niemand angemeldet,
 * bleibt der Baustein vollständig weg (globale Vorgabe leerer Bausteine).
 */
export default function NutzungWegeJetzt() {
    const nutzungFilters = useNutzungFilter();
    const {filters} = nutzungFilters;
    const abrufUrl = useMemo(() => baueUrl(filters), [filters.user_id, filters.bereich]);

    const initial = useStatisticsData(abrufUrl, {});
    const [personen, setPersonen] = useState(null);

    useEffect(() => {
        if (initial.data) {
            setPersonen(initial.data);
        }
    }, [initial.data]);

    usePollingWhenVisible(abrufUrl, TAKT_MS, (frisch) => setPersonen(frisch));

    const rows = Array.isArray(personen) ? personen.filter((person) => stationenVon(person).length > 0) : [];
    const loading = initial.loading && personen === null;

    return (
        <Container fluid className="pt-2 pb-4">
            <NutzungLayout gruppe="Jetzt" titel="Aktuelle Wege" nutzungFilters={nutzungFilters}>
                <NutzungWidget
                    title="Aktuelle Wege"
                    help="Je angemeldeter Person die letzten Stationen ihres heutigen Wegs - aktualisiert sich automatisch alle 15 Sekunden, solange dieses Fenster sichtbar ist."
                    loading={loading}
                    error={initial.error}
                    empty={!loading && !initial.error && rows.length === 0}
                    height={Math.max(160, Math.ceil(rows.length / 3) * 210)}
                >
                    <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
                        {rows.map((person) => (
                            <div className="col" key={person.user_id}>
                                <PersonKarte person={person} />
                            </div>
                        ))}
                    </div>
                </NutzungWidget>
            </NutzungLayout>
        </Container>
    );
}
